import { useState, type ReactNode } from 'react'
import { ApiClientError } from 'grabbitbot-legacy-ui'
import type { MoneyFeedback } from '../lib/useMoneyAction'
import { describeError } from '../api/errorMessages'

// Components only (hooks/logic live in src/lib/*) — keeps
// `react-refresh/only-export-components` quiet, same split as
// secure.grabbitbot.com's adminShared.tsx / adminHelpers.ts.

export function Notice({ feedback }: { feedback: MoneyFeedback | undefined | null }) {
  if (!feedback) return null
  return (
    <p role={feedback.tone === 'error' ? 'alert' : 'status'} className={`portal-notice portal-notice--${feedback.tone}`}>
      {feedback.text}
    </p>
  )
}

/** Loading / error / empty wrapper around a `useApiQuery` result. */
export function QueryState({
  loading,
  error,
  children,
}: {
  loading: boolean
  error: unknown
  children: ReactNode
}) {
  if (error) {
    const status = error instanceof ApiClientError ? error.status : 0
    const text =
      status === 403
        ? 'You do not have access to this project or page.'
        : status === 410
          ? 'Not found.'
          : describeError(error, 'Loading failed')
    return (
      <p role="alert" className="portal-notice portal-notice--error">
        {text}
      </p>
    )
  }
  if (loading) return <p className="portal-text-muted">Loading…</p>
  return <>{children}</>
}

export function PageTitle({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="portal-page-title">
      <h1>{title}</h1>
      {children && <div className="portal-inline-actions">{children}</div>}
    </div>
  )
}

/** Inline two-step confirmation — replaces the legacy `confirm()` dialogs
 * (the texts are kept verbatim as `pendingText`). Not a modal: the second
 * click surface sits next to the button that triggered it. */
export function ConfirmButton({
  label,
  confirmLabel = 'Yes, confirm',
  pendingText,
  disabled,
  busy,
  onConfirm,
  className,
}: {
  label: ReactNode
  confirmLabel?: string
  pendingText: ReactNode
  disabled?: boolean
  busy?: boolean
  onConfirm: () => void
  className?: string
}) {
  const [confirming, setConfirming] = useState(false)
  if (confirming && !busy) {
    return (
      <span className="portal-confirm-inline" role="group" aria-label="Confirm action">
        <span>{pendingText}</span>
        <button
          type="button"
          onClick={() => {
            setConfirming(false)
            onConfirm()
          }}
        >
          {confirmLabel}
        </button>
        <button type="button" onClick={() => setConfirming(false)}>
          Cancel
        </button>
      </span>
    )
  }
  return (
    <button type="button" className={className} disabled={disabled || busy} onClick={() => setConfirming(true)}>
      {busy ? 'Working…' : label}
    </button>
  )
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string }[]
  active: T
  onChange: (id: T) => void
}) {
  return (
    <nav className="portal-tabs" role="tablist" aria-label="Sections">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={active === tab.id ? 'portal-tab portal-tab--active' : 'portal-tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  )
}
